import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { api } from '../services/api';

/**
 * Página para configurar senha
 * Acessada via link enviado por email
 */

const SetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');

  // Valida token ao carregar
  useEffect(() => {
  const validateToken = async () => {
    if (!token) {
      setError('Link inválido. Solicite um novo link ao administrador.');
      setLoading(false);
      return;
    }

    try {
      const result = await api.validateSetupToken(token);
      setTokenValid(true);

      // backend retorna { valid: true, email: "..." }
      setEmail(result?.email || '');
      setError(null);
    } catch (err) {
      setTokenValid(false);
      setError(err?.message || 'Link inválido ou expirado. Solicite um novo link.');
    } finally {
      setLoading(false);
    }
  };

  validateToken();
}, [token]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validações
    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }
    
    setSaving(true);
    
   try {
  await api.setPassword(token, password);
  setSuccess(true);

  setTimeout(() => {
    navigate('/');
  }, 3000);

} catch (err) {
  setError(err?.message || 'Não foi possível configurar a senha. Tente novamente.');
} finally {
  setSaving(false);
}

  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#0056D2] animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Validando link...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center p-4">
        <div className="bg-[#0A0A12] border border-white/10 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Senha Configurada!</h1>
          <p className="text-slate-400 mb-6">
            Sua senha foi criada com sucesso. Você será redirecionado para o login...
          </p>
          <Loader2 className="w-6 h-6 text-[#0056D2] animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Error state (token inválido)
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center p-4">
        <div className="bg-[#0A0A12] border border-white/10 rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Link Inválido</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#0056D2] hover:bg-[#0066FF] text-white rounded-xl transition-colors"
          >
            Ir para Login
          </button>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="min-h-screen bg-[#05050A] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-[#0A0A12]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#0056D2]/10 border border-[#0056D2]/30 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#0056D2]" />
            </div>
            <h1 className="text-xl font-bold text-white font-['Manrope']">
              Criar Senha
            </h1>
            <p className="text-slate-400 text-sm mt-2">
              Configure sua senha para acessar a plataforma
            </p>
            {email && (
              <p className="text-[#0056D2] text-sm mt-1">{email}</p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Nova Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                  className="w-full pl-10 pr-12 py-3 bg-[#12121A] border border-white/10 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-[#0056D2]/30 focus:border-[#0056D2]/50 transition-all"
                  data-testid="set-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Confirmar Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Digite novamente"
                  required
                  className="w-full pl-10 pr-4 py-3 bg-[#12121A] border border-white/10 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-[#0056D2]/30 focus:border-[#0056D2]/50 transition-all"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={saving || !password || !confirmPassword}
              className="w-full py-3.5 bg-[#0056D2] hover:bg-[#0066FF] text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-[#0056D2]/25"
              data-testid="set-password-submit"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Configurando...</span>
                </>
              ) : (
                <span>Criar Senha</span>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetPasswordPage;
